import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { LeadForm } from "./LeadForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { PHONE, PHONE_HREF } from "../data/site";
import { SERVICE_PROCESS } from "../data/servicePages";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function ServicePage({ page }) {
  const slug = page.path.replace(/\//g, "");

  useEffect(() => {
    document.title = `${page.seoTitle} | Cando Construction Services Ltd.`;
    return () => {
      document.title = "Cando Construction Services Ltd. | Edmonton Residential Construction";
    };
  }, [page]);

  return (
    <main data-testid={`service-page-${slug}`} className="bg-warm">
      {/* HERO */}
      <section className="relative overflow-hidden pt-20" data-testid={`service-hero-${slug}`}>
        <div className="container-cando grid items-center gap-12 pb-16 pt-12 lg:grid-cols-12 lg:pb-24 lg:pt-16">
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <Eyebrow>{page.eyebrow}</Eyebrow>
            </motion.div>
            <h1 className="h-display mt-8 text-charcoal" data-testid={`service-h1-${slug}`}>
              <Line delay={0.3}>{page.h1}</Line>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65 }}
              className="lead mt-7 max-w-md text-mute"
            >
              {page.heroCopy}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-10 flex flex-col gap-4 sm:flex-row"
            >
              <Link to="#estimate" data-testid={`service-cta-estimate-${slug}`} className="btn btn-primary group">
                Get a Project Estimate
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a href={PHONE_HREF} data-testid={`service-cta-call-${slug}`} className="btn btn-outline">
                <Phone size={15} />
                Call {PHONE}
              </a>
            </motion.div>
          </div>
          <div className="lg:col-span-6">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <motion.img
                src={page.heroImg}
                alt={page.seoTitle}
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="aspect-[4/3] w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="section-pad border-t border-charcoal/10 bg-warm">
        <div className="container-cando grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>What We Do</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="h2 mt-6 text-charcoal">{page.introTitle}</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            {page.intro.map((p, i) => (
              <Reveal key={i} delay={0.12 + i * 0.08}>
                <p className="body-lg mt-2 max-w-2xl text-mute first:mt-0 lg:mt-0 lg:[&:not(:first-child)]:mt-5">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUDED */}
      <section className="section-pad bg-warm2" data-testid={`service-included-${slug}`}>
        <div className="container-cando">
          <Reveal>
            <Eyebrow>Services Included</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="h2 mt-6 max-w-2xl text-charcoal">What's Included.</h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-charcoal/10 bg-charcoal/10 sm:grid-cols-2 lg:grid-cols-4">
            {page.included.map((s, i) => (
              <Reveal key={s.t} delay={(i % 4) * 0.07} className="bg-warm2">
                <div className="h-full p-8 transition-colors duration-500 hover:bg-warm" data-testid={`service-included-item-${slug}-${i}`}>
                  <span className="font-heading text-[14px] font-bold tracking-[0.2em] text-bronze">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="h4 mt-4 !text-[19px] text-charcoal">{s.t}</h3>
                  <p className="mt-2.5 text-[14px] leading-relaxed text-mute">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="grain section-pad relative bg-charcoal">
        <div className="container-cando relative">
          <Reveal>
            <Eyebrow dark>Why Cando</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="h2 mt-6 max-w-2xl text-warm">A Better Construction Experience.</h2>
          </Reveal>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {page.benefits.map((b, i) => (
              <Reveal key={b.t} delay={i * 0.08}>
                <div className="border-t-2 border-warm/15 pt-6" data-testid={`service-benefit-${slug}-${i}`}>
                  <span className="font-heading text-[14px] font-bold tracking-[0.2em] text-bronze">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-heading text-[18px] font-semibold text-warm">{b.t}</h3>
                  <p className="mt-2.5 text-[14px] leading-relaxed text-warm/60">{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section-pad bg-warm">
        <div className="container-cando">
          <Reveal>
            <Eyebrow>Our Process</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="h2 mt-6 max-w-2xl text-charcoal">From Consultation to Final Walkthrough.</h2>
          </Reveal>
          <div className="mt-14 grid gap-10 md:grid-cols-3 lg:grid-cols-6 lg:gap-6" data-testid={`service-process-${slug}`}>
            {SERVICE_PROCESS.map((s, i) => (
              <Reveal key={s.key} delay={i * 0.08} className="relative">
                <div className="border-t-2 border-charcoal/15 pt-6">
                  <span className="absolute -top-[9px] left-0 h-4 w-4 rotate-45 bg-bronze" />
                  <span className="font-heading text-[36px] font-extrabold leading-none tracking-tight text-charcoal/15">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-heading text-[13px] font-bold uppercase tracking-[0.18em] text-charcoal">
                    {s.key}
                  </h3>
                  <p className="mt-2.5 text-[14px] leading-relaxed text-mute">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="section-pad bg-warm2">
        <div className="container-cando">
          <Reveal>
            <Eyebrow>Our Work</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="h2 mt-6 max-w-2xl text-charcoal">{page.galleryLabel}.</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {page.gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group overflow-hidden">
                  <img
                    src={g}
                    alt={`${page.service} project ${i + 1}`}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-warm">
        <div className="container-cando grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>Common Questions</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="h2 mt-6 max-w-md text-charcoal">{page.service} FAQs.</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <Accordion type="single" collapsible className="border-t border-charcoal/15" data-testid={`service-faq-${slug}`}>
                {page.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-b border-charcoal/15">
                    <AccordionTrigger className="py-6 text-left font-heading text-[17px] font-semibold text-charcoal hover:text-bronze hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 text-[16px] leading-relaxed text-mute">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="estimate" className="grain section-pad relative bg-charcoal" data-testid={`service-estimate-${slug}`}>
        <div className="container-cando relative grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow dark>Get Started</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="h2 mt-6 text-warm">Ready to Talk About Your Project?</h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="lead mt-6 max-w-md text-warm/65">
                Tell us what you're planning. We'll review your project and help you take the next step.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <a href={PHONE_HREF} data-testid={`service-form-call-${slug}`} className="link-arrow mt-8 !text-bronze">
                <Phone size={15} />
                Call {PHONE}
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <LeadForm projectType={page.service} slug={slug} dark />
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
